import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Subject } from 'rxjs'
import { map, startWith, takeUntil } from 'rxjs/operators'
import { Structure } from '../../models/dev.test.models'
import { ErrorService } from '../../services/error.service'
import { KeyingDataSets } from '../../services/keying.data.sets.service'
import { KeyingDataSetsService } from '../../services/keying.data.sets.service'
import { Loading, LoadingService } from '../../services/loading.service'
import { LoggingService } from '../../services/logging.service'

@Component({
    selector: 'app-new-structure-dialog',
    templateUrl: './new-structure-dialog.component.html',
    styleUrls: ['./new-structure-dialog.component.scss'],
    standalone: false
})
export class NewStructureDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  newStructureForm: FormGroup
  newBudgetKeySystemForm: FormGroup

  loading: Loading
  files

  countries = []
  filteredCountries
  selectedCountry

  states
  selectedState

  combinatingRuleSets = []
  filteredCombinatingRuleSets
  selectedCombinatingRuleSet

  constructionTypes = []
  filteredConstructionKeyTypes
  availableConstructionKeyTypes
  selectedConstructionKeyType

  keywayFamilies = []
  filteredKeywayFamilies
  selectedKeywayFamily

  chamberTypes = []
  filteredChamberTypes
  availableChamberTypes
  selectedChamberType

  defaultTMKName = 'TMK'
  // steps = [
  //   { name: 'Structure', current: true, complete: false, step: 0 },
  //   { name: 'Systems', current: false, complete: false, step: 1 },
  // ];
  // step = this.steps[0];

  focus = {
    jobName: false,
    city: false,
    state: false,
    country: false,
    combinatingRuleSet: false,
    constructionKeyType: false,
    keywayFamily: false,
    chamberType: false,
    numberOfGrandMasters: false,
    numberOfMasters: false,
    numberOfSubMasters: false,
    numberOfChanges: false,
    tmkName: false,
  }

  public errorMessages = {
    jobName: [
      { type: 'required', message: 'A Job name is required.' },
      { type: 'pattern', message: 'Asterisks cannot be used in job names.' },
      {
        type: 'maxlength',
        message: 'Please reduce your job name length to be under the allowed 100 characters.',
      },
    ],
    city: [{ type: 'required', message: 'A City is required.' }],
    state: [{ type: 'required', message: 'A State is required.' }],
    country: [{ type: 'required', message: 'A Country is required.' }],
    combinatingRuleSet: [{ type: 'required', message: 'A Combinating Rule Set is required.' }],
    constructionKeyType: [{ type: 'required', message: 'A Construction Key Type is required.' }],
    keywayFamily: [{ type: 'required', message: 'A Keyway Family is required.' }],
    tmkName: [{ type: 'required', message: 'TMK Name is required.' }],
    chamberType: [{ type: 'required', message: 'TMK Name is required.' }],
  }

  isDealerKeySystem = false

  constructor(
    private dialogRef: MatDialogRef<NewStructureDialogComponent>,
    public form: FormBuilder,
    public keyingDataSetsService: KeyingDataSetsService,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    @Inject(MAT_DIALOG_DATA) public data,
    private loggingService: LoggingService
  ) {
    this.newStructureForm = this.form.group({
      jobName: new FormControl('', [Validators.required, Validators.maxLength(100), Validators.pattern(/^[^\*]+$/)]),
      city: new FormControl('', [Validators.required]),
      state: new FormControl({ value: '', disabled: true }, [Validators.required]),
      country: new FormControl('', [Validators.required]),
      combinatingRuleSet: new FormControl({ value: '', disabled: !this.data }, [Validators.required]),
      constructionKeyType: new FormControl({ value: '', disabled: !this.data }, [Validators.required]),
      keywayFamily: new FormControl({ value: '', disabled: this.data }, [Validators.required]),
      chamberType: new FormControl({ value: '', disabled: true }, [Validators.required]),
      tmkName: new FormControl('', this.data ? [] : [Validators.required]),
      hasDocuments: new FormControl(false, [Validators.required]),
    })
  }

  /* istanbul ignore next */
  ngOnInit(): void {
    this.loadingService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.loading = loading
    })

    this.newStructureForm.controls.country.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((country) => {
      this.states = country?.states ?? []

      if (this.states.length > 0) {
        this.newStructureForm.controls.state.setValidators([Validators.required])
        this.newStructureForm.controls.state.enable()
      } else {
        this.newStructureForm.controls.state.setValidators([])
        this.newStructureForm.controls.state.disable()
      }

      this.newStructureForm.controls.state.setValue(null)
      this.newStructureForm.controls.state.updateValueAndValidity()
    })

    this.prepareFormData()
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  prepareFormData() {
    this.keyingDataSetsService.keyingDataSets$.pipe(takeUntil(this.destroy$)).subscribe(
      (keyingDataSets: KeyingDataSets) => {
        if (!keyingDataSets) {
          return
        }

        this.countries = keyingDataSets.countries
        this.newStructureForm.controls.country.setValue(this.countries[0])
        const upgradedSystemFamilies = ['Schlage Everest C/S', 'Schlage Everest D/T', 'Schlage Everest B/R']

        this.keywayFamilies = keyingDataSets.keywayFamilies.filter((family) => {
          return !upgradedSystemFamilies.includes(family.name)
        })
        this.combinatingRuleSets = keyingDataSets.combinatingRuleSets
        this.constructionTypes = keyingDataSets.constructionTypes

        this.chamberTypes = keyingDataSets.chamberTypes
        this.availableChamberTypes = this.chamberTypes

        this.newStructureForm.controls.constructionKeyType.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            this.checkTmkNameStatus()
          })

        this.newStructureForm.controls.keywayFamily.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
          if (!value) return
          if (!this.data) {
            this.newStructureForm.controls.combinatingRuleSet.enable()
            this.newStructureForm.controls.chamberType.enable()
            this.newStructureForm.controls.constructionKeyType.enable()
          }
          this.keywayFamilyChanged()
        })
        const family = this.keywayFamilies.find((family) => family.id === this.data?.keywayFamilyId)
        this.selectedKeywayFamily = family
        this.newStructureForm.controls.keywayFamily.setValue(family)

        this.newStructureForm.controls.combinatingRuleSet.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            this.selectedCombinatingRuleSet = value
            this.getConstructionTypesFromAttributes()
          })

        this.newStructureForm.controls.chamberType.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
          this.getConstructionTypesFromAttributes()
        })
        const chamberType = this.chamberTypes.find((type) => type.id === this.data?.chamberTypeId)
        this.selectedChamberType = chamberType
        this.newStructureForm.controls.chamberType.setValue(chamberType)

        this.selectedConstructionKeyType = this.constructionTypes[0]
        this.newStructureForm.controls.constructionKeyType.setValue(this.selectedConstructionKeyType)

        this.keyingDataSetsService.combinatingRuleSets.pipe(takeUntil(this.destroy$)).subscribe((res) => {
          if (!res) {
            return
          }
          this.combinatingRuleSets = res.combinatingRuleSets
          this.selectedCombinatingRuleSet = this.combinatingRuleSets[0]
          this.newStructureForm.controls.combinatingRuleSet.setValue(this.selectedCombinatingRuleSet)
        })
      },
      (error) => {
        this.errorService.throw(error)
        this.close()
      }
    )
  }

  /* istanbul ignore next */
  displayName(subject) {
    if (subject.name) {
      return (subject && subject.name) || subject
    } else if (subject.description) {
      return (subject && subject.description) || subject
    }
  }

  // Construction Key Type
  /* istanbul ignore next */
  filterConstructionKeyType(value) {
    return value && value.length
      ? this.constructionTypes
          .filter((subject) => {
            return subject.description.toLowerCase().includes(value.toLowerCase())
          })
          .sort((subject1, subject2) => {
            return subject1.description.localeCompare(subject2.description)
          })
      : this.constructionTypes
  }

  // Keyway Family
  /* istanbul ignore next */
  filterKeywayFamily(value) {
    return value && value.length
      ? this.keywayFamilies
          .filter((subject) => {
            return subject.name.toLowerCase().includes(value.toLowerCase())
          })
          .sort((subject1, subject2) => {
            return subject1.name.localeCompare(subject2.name)
          })
      : this.keywayFamilies
  }

  // Chamber Type
  /* istanbul ignore next */
  filterChamberTypes(value) {
    return value && value.length
      ? this.availableChamberTypes
          .filter((subject) => {
            return subject.name.toLowerCase().includes(value.toLowerCase())
          })
          .sort((subject1, subject2) => {
            return subject1.name.localeCompare(subject2.name)
          })
      : this.availableChamberTypes
  }

  /* istanbul ignore next */
  checkBox(event) {
    this.newStructureForm.controls.hasDocuments.value.set(event)
  }

  // Files
  /* istanbul ignore next */
  handleFileSelect(event: File[]): void {
    this.loggingService.log('file selected: ', event)
    this.files = event
  }

  // Forms
  /* istanbul ignore next */
  createStructure() {
    if (this.newStructureForm.valid) {
      const controls = this.newStructureForm.controls

      const data = {
        structure: {
          id: this.data?.id,
          name: controls.jobName.value.toUpperCase(),
          city: controls.city.value.toUpperCase(),
          stateCode: controls.state.value?.code,
          countryCode: controls.country.value.code,
          keyWayFamilyId: controls.keywayFamily.value.id,
          combinatingRuleSetId: controls.combinatingRuleSet.value.id,
          chamberTypeId: controls.chamberType.value.id,
          hasDocuments: controls.hasDocuments.value,
        },
        keySystem: {
          id: this.data?.keySystems[0].id,
          topMasterKeyName: controls.tmkName.value,
          constructionTypeId: controls.constructionKeyType.value.id,
          manufacturerId: 1,
          isDealerKeySystem: this.isDealerKeySystem,
        },
        masterKeys: {
          numberOfGrandMasters: 1,
          numberOfMasters: 1,
          numberOfSubMasters: 1,
          numberOfChanges: 1,
        },
      }

      this.dialogRef.close(data)
    }
  }

  tmkHasDefault() {
    const retVal =
      this.newStructureForm.controls.keywayFamily.value &&
      this.selectedConstructionKeyType?.id == 3 &&
      (!this.newStructureForm.controls.tmkName.value ||
        this.newStructureForm.controls.tmkName.value == this.defaultTMKName)
    return retVal
  }

  getChamberTypesFromAttributes() {
    this.availableChamberTypes = []
    this.selectedKeywayFamily.chamberTypeAttributes.forEach((e) =>
      this.availableChamberTypes.push(this.chamberTypes.find((c) => c.id === e.chamberTypeId))
    )
  }

  getConstructionTypesFromAttributes() {
    if (!this.selectedKeywayFamily || !this.selectedChamberType) return
    this.availableConstructionKeyTypes = []
    this.constructionTypes.forEach((e) => {
      const t = e.attributes.filter(
        (a) => a.keywayFamilyId === this.selectedKeywayFamily.id && a.chamberTypeId === this.selectedChamberType.id
      )
      t.forEach((tt) =>
        this.availableConstructionKeyTypes.push(this.constructionTypes.find((c) => c.id === tt.constructionTypeId))
      )
    })

    if (
      this.selectedKeywayFamily &&
      this.selectedCombinatingRuleSet &&
      this.selectedKeywayFamily.id === 11 &&
      this.selectedCombinatingRuleSet.id === 8
    ) {
      this.availableConstructionKeyTypes = this.availableConstructionKeyTypes.filter((x) => x.id !== 6)
    }
    this.selectedConstructionKeyType = this.availableConstructionKeyTypes[0]
    this.newStructureForm.controls.constructionKeyType.setValue(this.selectedConstructionKeyType)
  }

  keywayFamilyChanged() {
    this.keyingDataSetsService.loadCombinatingRuleSets(this.selectedKeywayFamily.id)
    this.getChamberTypesFromAttributes()
    this.getConstructionTypesFromAttributes()

    const def = this.selectedKeywayFamily.chamberTypeAttributes.find((a) => a.isDefault)
    this.selectedChamberType = this.availableChamberTypes.find((a) => a.id === def.chamberTypeId)
    this.newStructureForm.controls.chamberType.setValue(this.selectedChamberType)
    this.checkTmkNameStatus()
  }
  checkTmkNameStatus() {
    if (this.tmkHasDefault()) {
      this.newStructureForm.controls.tmkName.setValue(this.defaultTMKName)
      this.newStructureForm.controls.tmkName.disable()
    } else {
      this.newStructureForm.controls.tmkName.enable()
    }
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  compareObjs(o1: any, o2: any) {
    return o1 && o2 && o1.id === o2.id
  }

  // Dialog
  /* istanbul ignore next */
  close() {
    this.newStructureForm.reset()
    this.dialogRef.close()
  }

  onDealerKeySystemChange() {
    this.isDealerKeySystem = !this.isDealerKeySystem
    if (this.isDealerKeySystem) {
      this.newStructureForm.controls.tmkName.setValue('Dealer Spec')
      this.newStructureForm.controls.tmkName.disable()
    } else {
      this.newStructureForm.controls.tmkName.setValue('')
      this.newStructureForm.controls.tmkName.enable()
    }
  }
}
